import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "../../firebase/authService";
import dbService from "../../firebase/dbService";
import { Timestamp } from "firebase/firestore";


export const register = createAsyncThunk(
  "auth/register",
  async (
    { email, password, name, docId, data, photoUrl },
    { rejectWithValue }
  ) => {
    try {
      // 1. Create a new auth in firebase auth
      const user = await authService.createAccount({
        email,
        password,
        name,
        photoUrl,
      });


      //2. add user auth data to firestore
      const registerData = {
        ...data,
        createdAt: Timestamp.now(),
        modifiedAt: Timestamp.now(),
        uid: user.uid,
      };

      const created = await dbService.createDocumentWithId(
        `${data.accountType}s`,
        registerData,
        docId
      );
      if (!created) {
        // If Firestore write failed, roll back auth user
        try {
          await authService.deleteCurrentUser();
        } catch (_) {
          // ignore secondary rollback errors
        }
        throw new Error("Failed to save user profile. Please try again.");
      }

      //3. get the current user (registered now) details from firestore
      const userAuth = await authService.getCurrentUser();
      const parenData = await dbService.getAuthDetails('parents', userAuth.uid)
      const schoolData = await dbService.getAuthDetails('schools', userAuth.uid)
      const userData =
        data.accountType === "parent"
          ? {
            uid: userAuth.uid,
            email: userAuth.email,
            name: userAuth.displayName,
            phone: data?.phone || "",
            accountType: parenData?.accountType || "",
            // id: docId,
            ...parenData
          }
          : data.accountType === "school"
            ? {
              uid: userAuth.uid,
              email: userAuth.email,
              name: userAuth.displayName,
              phone: schoolData?.phone || "",
              accountType: schoolData?.accountType || "",
              ...schoolData,
              // id: docId,
            }
            : {};

      let registeredSchoolDetails;
      if (data?.accountType === 'parent') {
        const schoolId = data?.schoolId;
        if (
          schoolId
        ) {
          const schoolData = await dbService?.getdocument('schools', schoolId)
          registeredSchoolDetails = {
            schoolId: schoolData.id,
            schoolName: schoolData.schoolName,
            schoolAddress: schoolData.schoolAddress,
            schoolLogoUrl: schoolData.schoolLogoUrl,
            fee: schoolData.fee.total
          }
        }
      }

      return { userData, registeredSchoolDetails };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      let userAuth = await authService.getCurrentUser();

      if (!userAuth) {
        try {
          userAuth = await authService.login({ email, password });
        } catch (error) {
          throw new Error(error.message);
        }
      }
      let userDetails = [];
      // Fetch user details from Firestore (schools collection)
      const schoolDetails = await dbService
        .getAuthDetails("schools", userAuth.uid)
        .catch(() => {
          throw new Error("User not found");
        });
      userDetails.push(schoolDetails || null);

      // Fetch user details from Firestore (parents collection)
      const parentDetails = await dbService
        .getAuthDetails("parents", userAuth.uid)
        .catch(() => {
          throw new Error("User not found");
        });
      userDetails.push(parentDetails || null);

      const data =
        userDetails?.filter((details) => details !== null)?.[0] || null;
      if (!data) {
        throw new Error("User not found");
      }
      const userData =
        data.accountType === "parent"
          ? {
            uid: userAuth.uid,
            email: userAuth.email,
            name: userAuth.displayName,
            phone: data?.phone || "",
            accountType: data?.accountType || "",
            ...data
          }
          : data.accountType === "school"
            ? {
              uid: userAuth.uid,
              email: userAuth.email,
              name: userAuth.displayName,
              phone: data?.phone || "",
              accountType: data?.accountType || "",
              ...data,
            }
            : {};

      let registeredSchoolDetails;
      if (data?.accountType === 'parent') {
        const schoolId = data?.schoolId;
        if (
          schoolId
        ) {
          const schoolData = await dbService?.getdocument('schools', schoolId)
          registeredSchoolDetails = {
            schoolId: schoolData.id,
            schoolName: schoolData.schoolName,
            schoolAddress: schoolData.schoolAddress,
            schoolLogoUrl: schoolData.schoolLogoUrl,
            fee: schoolData.fee.total
          }
        }
      }
      return { userData, registeredSchoolDetails };
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  userDetails: null,
  schoolDetails: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    updateUserDetails: (state, action) => {
      state.userDetails = {
        ...state.userDetails,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.userDetails = action.payload.userData;
        state.schoolDetails = action.payload.registeredSchoolDetails
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.userDetails = action.payload.userData;
        state.schoolDetails = action.payload.registeredSchoolDetails
        state.isAuthenticated = true;
        state.loading = false;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.userDetails = null;
        state.schoolDetails = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { updateUserDetails } = authSlice.actions;

export default authSlice.reducer;
